import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { IncomeStatementReportModel } from '../../../../core/models/data/income-statement-report.model';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { DateUtilities, DateFormat } from '../../../../core/utilities/date.utilities';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { ReportDateFilterComponent } from '../../components/report-date-filter/report-date-filter.component';
import { GenericMessagingConstants } from '../../../../core/constants/messaging.constants';

@Component({
	selector: 'ms-income-statement',
	standalone: true,
	imports: [CommonModule, FaDuotoneIconComponent, ReportDateFilterComponent, OrgDatePipe],
	templateUrl: './income-statement.page.html',
	styleUrl: './income-statement.page.scss'
})
export class IncomeStatementPage implements OnInit {

	private financialService = inject(FinancialService);
	private userSessionService = inject(UserSessionService);

	public readonly startDate = signal<string>(DateUtilities.format(new Date(new Date().getFullYear(), 0, 1), DateFormat.ISODate));
	public readonly endDate = signal<string>(DateUtilities.todayISO());
	public readonly report = signal<IncomeStatementReportModel | undefined>(undefined);
	public readonly isLoading = signal<boolean>(false);
	public readonly errorMessage = signal<string>('');

	public async ngOnInit(): Promise<void> {
		await this.loadReport();
	}

	public async onDateChanged(): Promise<void> {
		if (!this.startDate() || !this.endDate()) return;

		if (this.startDate() > this.endDate()) {
			this.errorMessage.set('Start date must be on or before the end date.');
			this.report.set(undefined);
			return;
		}

		await this.loadReport();
	}

	public formatAmount(amount: number): string {
		const current = this.report();
		if (!current) return '';
		return CurrencyUtilities.convertMinorUnitToDisplayFormat(amount, current.currency);
	}

	private async loadReport(): Promise<void> {
		const organization = this.userSessionService.activeOrganization();
		if (!organization) {
			this.report.set(undefined);
			this.errorMessage.set('An active organization is required to view this report.');
			return;
		}

		this.isLoading.set(true);
		this.errorMessage.set('');

		const result = await this.financialService.getIncomeStatement(
			organization.organizationId,
			this.startDate(),
			this.endDate()
		);

		if (result.success && result.data) {
			this.report.set(result.data);
		} else {
			this.report.set(undefined);
			this.errorMessage.set(result.message || GenericMessagingConstants.GENERIC_ERROR_OCCURRED());
		}

		this.isLoading.set(false);
	}

}
